import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../scripts/api';

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

// ── Section editor ──────────────────────────────────────────────────────────────
function SectionEditor({ section, sIdx, onUpdate, onDelete, onMoveUp, onMoveDown, onPromoteStep, isFirst, isLast }) {
  const updateStep = (stIdx, field, val) => {
    const steps = section.steps.map((s, i) => i === stIdx ? { ...s, [field]: val } : s);
    onUpdate({ ...section, steps });
  };
  const addStep = () => {
    // Handle both legacy plain-integer IDs ("1", "2") and composite ids ("3-5").
    const maxNum = section.steps.reduce((acc, s) => {
      const raw = String(s.id || '');
      const m = raw.match(/-(\d+)$/);
      const n = m ? parseInt(m[1], 10) : parseInt(raw, 10);
      return Number.isFinite(n) && n > acc ? n : acc;
    }, 0);
    const nextNum = maxNum + 1;
    const newId = `${sIdx}-${nextNum}`;
    onUpdate({ ...section, steps: [...section.steps, { id: newId, num: String(nextNum), item: '', instruction: '' }] });
  };
  const deleteStep = (stIdx) => onUpdate({ ...section, steps: section.steps.filter((_, i) => i !== stIdx) });
  const moveStep = (stIdx, dir) => {
    const steps = [...section.steps];
    const target = stIdx + dir;
    if (target < 0 || target >= steps.length) return;
    [steps[stIdx], steps[target]] = [steps[target], steps[stIdx]];
    onUpdate({ ...section, steps });
  };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
      {/* Section header */}
      <div style={{ background: '#1e293b', color: '#fff', padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center' }}>
        <input
          value={section.title}
          onChange={e => onUpdate({ ...section, title: e.target.value })}
          style={{ flex: 1, background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '4px 8px', color: '#fff', fontSize: 14, fontWeight: 700 }}
          placeholder="Section title"
        />
        <button onClick={onMoveUp}   disabled={isFirst} style={arrowBtn}>↑</button>
        <button onClick={onMoveDown} disabled={isLast}  style={arrowBtn}>↓</button>
        <button onClick={onDelete} style={{ ...arrowBtn, background: '#dc2626', color: '#fff' }}>✕</button>
      </div>

      {/* Steps */}
      <div style={{ padding: 12 }}>
        {section.steps.map((step, stIdx) => (
          <div key={stIdx} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1fr 28px 28px 28px 28px', gap: 6, marginBottom: 8, alignItems: 'flex-start' }}>
            <input
              value={step.id}
              onChange={e => updateStep(stIdx, 'id', e.target.value)}
              style={{ ...inputStyle, textAlign: 'center', fontWeight: 700 }}
              placeholder="#"
            />
            <input
              value={step.item}
              onChange={e => updateStep(stIdx, 'item', e.target.value)}
              style={inputStyle}
              placeholder="Item name (e.g. VIN ACCURACY)"
            />
            <input
              value={step.instruction}
              onChange={e => updateStep(stIdx, 'instruction', e.target.value)}
              style={inputStyle}
              placeholder="Instruction / notes (optional)"
            />
            <button
              onClick={() => onPromoteStep(stIdx)}
              title="Promote to section — splits this section here, moving this row and everything below into a new section titled with this row's item name"
              style={{ ...smallBtn, color: '#1d4ed8', borderColor: '#93c5fd', background: '#eff6ff', fontWeight: 700 }}
            >⇪</button>
            <button onClick={() => moveStep(stIdx, -1)} disabled={stIdx === 0}                       style={smallBtn}>↑</button>
            <button onClick={() => moveStep(stIdx,  1)} disabled={stIdx === section.steps.length - 1} style={smallBtn}>↓</button>
            <button onClick={() => deleteStep(stIdx)}                                                 style={{ ...smallBtn, color: '#dc2626' }}>✕</button>
          </div>
        ))}
        <button onClick={addStep} style={{ marginTop: 4, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#64748b', fontWeight: 700 }}>
          + Add Step
        </button>
      </div>
    </div>
  );
}

const inputStyle = { border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', fontSize: 13, width: '100%' };
const smallBtn   = { border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 6px', cursor: 'pointer', background: '#f8fafc', fontSize: 12 };
const arrowBtn   = { border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, padding: '3px 7px', cursor: 'pointer', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 13 };

// ────────────────────────────────────────────────────────────────────────────────
export default function PdiTemplatePage() {
  const nav = useNavigate();

  const [templates,     setTemplates]     = useState([]);
  const [refFiles,      setRefFiles]      = useState([]);
  const [busy,          setBusy]          = useState(false);
  const [msg,           setMsg]           = useState('');

  // editor state
  const [editing,       setEditing]       = useState(false);   // false | 'new' | 'import' | template_key
  const [editTitle,     setEditTitle]     = useState('');
  const [editVendor,    setEditVendor]    = useState('');
  const [editVersion,   setEditVersion]   = useState('');
  const [editKey,       setEditKey]       = useState('');
  const [editSections,  setEditSections]  = useState([]);
  const [saveBusy,      setSaveBusy]      = useState(false);
  const [preview,       setPreview]       = useState(null); // template object to preview

  // import state
  const [importPath,    setImportPath]    = useState('');
  const [importBusy,    setImportBusy]    = useState(false);

  // load template list + reference files
  const loadAll = async () => {
    setBusy(true);
    try {
      const [tplRes, refRes] = await Promise.all([
        api.get('/pdi/templates'),
        api.get('/pdi/reference-files').catch(() => ({ data: { files: [] } })),
      ]);
      setTemplates(tplRes.data.templates || []);
      setRefFiles(refRes.data.files || []);
    } catch (e) {
      setMsg(`Load failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // open blank editor
  const openNew = () => {
    setEditTitle(''); setEditVendor(''); setEditVersion(''); setEditKey('');
    setEditSections([{ title: 'Section 1', steps: [{ id: '1', item: '', instruction: '' }] }]);
    setMsg('');
    setEditing('new');
  };

  // open editor for existing DB template
  const openEdit = async (tpl) => {
    if (tpl.source === 'file') { setMsg('Built-in file templates cannot be edited here. Import a copy to customise it.'); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/pdi/templates/${tpl.template}`);
      setEditTitle(data.title || '');
      setEditVendor(data.vendor || '');
      setEditVersion(data.version || '');
      setEditKey(data.template || tpl.template);
      setEditSections(data.sections || []);
      setMsg('');
      setEditing(tpl.template);
    } catch (e) {
      setMsg(`Load failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // import XLSX → populate editor
  const importXlsx = async () => {
    if (!importPath) return;
    setImportBusy(true); setMsg('');
    try {
      const { data } = await api.post('/pdi/reference-files/parse', { relPath: importPath });
      const guessedName = importPath.replace(/.*[/\\]/, '').replace(/\.(xlsx|xls)$/i, '').replace(/[_-]/g, ' ');
      setEditTitle(guessedName);
      setEditVendor('');
      setEditVersion('');
      setEditKey(slugify(guessedName));
      setEditSections(data.sections || []);
      setEditing('import');
      setMsg(`Imported ${data.sections?.length || 0} sections from ${importPath.replace(/.*[/\\]/, '')}. Review and save.`);
    } catch (e) {
      setMsg(`Import failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setImportBusy(false);
    }
  };

  const saveTemplate = async () => {
    if (!editTitle.trim()) { setMsg('Title is required.'); return; }
    const key = editKey.trim() || slugify(editTitle);
    if (!key) { setMsg('Template key could not be generated.'); return; }
    setSaveBusy(true); setMsg('');
    try {
      await api.post('/pdi/templates', {
        template_key: key,
        title: editTitle.trim(),
        vendor: editVendor.trim(),
        version: editVersion.trim(),
        sections: editSections,
      });
      setMsg('Saved.');
      setEditing(false);
      await loadAll();
    } catch (e) {
      setMsg(`Save failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setSaveBusy(false);
    }
  };

  const deleteTemplate = async (key) => {
    if (!window.confirm(`Delete template "${key}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/pdi/templates/${key}`);
      setMsg('Deleted.');
      await loadAll();
    } catch (e) {
      setMsg(`Delete failed: ${e?.response?.data?.error || e.message}`);
    }
  };

  const addSection = () => {
    const nextNum = editSections.length + 1;
    setEditSections([...editSections, { title: `Section ${nextNum}`, steps: [] }]);
  };

  const updateSection = (idx, sec) => setEditSections(editSections.map((s, i) => i === idx ? sec : s));
  const deleteSection = (idx) => setEditSections(editSections.filter((_, i) => i !== idx));
  const moveSection   = (idx, dir) => {
    const arr = [...editSections];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setEditSections(arr);
  };
  // Convert a step row into a section header: the chosen step's item name
  // becomes the title of a new section placed immediately after, and every
  // step at or below the chosen row moves into that new section.
  const promoteStepToSection = (sIdx, stIdx) => {
    const sec = editSections[sIdx];
    if (!sec) return;
    const step = sec.steps[stIdx];
    if (!step) return;
    const title = (step.item || '').trim() || 'New Section';
    const before = { ...sec, steps: sec.steps.slice(0, stIdx) };
    const afterSteps = sec.steps.slice(stIdx + 1).map((s, i) => {
      const n = i + 1;
      return { ...s, id: `${sIdx + 1}-${n}`, num: String(n) };
    });
    const after = { title, steps: afterSteps };
    const next = [...editSections];
    next.splice(sIdx, 1, before, after);
    setEditSections(next);
  };

  // ── editor view ─────────────────────────────────────────────────────────────
  if (editing !== false) {
    return (
      <div className="container">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ margin: 0 }}>
            {editing === 'new' || editing === 'import' ? 'New Template' : `Edit: ${editTitle}`}
          </h2>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn-outline" onClick={() => { setEditing(false); setMsg(''); }}>Cancel</button>
            <button className="btn" onClick={saveTemplate} disabled={saveBusy}>
              {saveBusy ? 'Saving…' : 'Save Template'}
            </button>
          </div>
        </div>

        {msg && <div style={{ marginBottom: 12, color: msg.startsWith('Saved') ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{msg}</div>}

        {/* Meta */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Template Key (unique ID)</div>
              <input className="input" value={editKey} onChange={e => setEditKey(slugify(e.target.value))} placeholder="auto-generated from title" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Title *</div>
              <input className="input" value={editTitle} onChange={e => { setEditTitle(e.target.value); if (!editKey) setEditKey(slugify(e.target.value)); }} placeholder="e.g. Tana Compactor PDI" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Vendor</div>
              <input className="input" value={editVendor} onChange={e => setEditVendor(e.target.value)} placeholder="e.g. TANA" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Version</div>
              <input className="input" value={editVersion} onChange={e => setEditVersion(e.target.value)} placeholder="e.g. 2025.01" />
            </div>
          </div>
        </div>

        {/* Sections */}
        {editSections.map((sec, idx) => (
          <SectionEditor
            key={idx}
            section={sec}
            sIdx={idx}
            onUpdate={(s) => updateSection(idx, s)}
            onDelete={() => deleteSection(idx)}
            onMoveUp={() => moveSection(idx, -1)}
            onMoveDown={() => moveSection(idx, 1)}
            onPromoteStep={(stIdx) => promoteStepToSection(idx, stIdx)}
            isFirst={idx === 0}
            isLast={idx === editSections.length - 1}
          />
        ))}

        <button
          onClick={addSection}
          style={{ width: '100%', border: '2px dashed #cbd5e1', borderRadius: 10, padding: '14px 0', cursor: 'pointer', background: '#f8fafc', fontWeight: 700, fontSize: 15, color: '#64748b' }}
        >
          + Add Section
        </button>
      </div>
    );
  }

  // ── list view ────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>PDI Templates</h2>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn-outline" onClick={() => nav('/pdi')}>← Back to PDI</button>
          <button className="btn" onClick={openNew}>+ New Template</button>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 12, color: '#dc2626', fontWeight: 600 }}>{msg}</div>}

      {/* Import from Reference XLSX */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Import from Reference Files</div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
          XLSX files from <code>backend/PDI_Reference/</code> — select one to parse and edit before saving.
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <select className="input" style={{ flex: 1, minWidth: 260 }} value={importPath} onChange={e => setImportPath(e.target.value)}>
            <option value="">— select a reference file —</option>
            {refFiles.map(f => (
              <option key={f.path} value={f.path}>{f.path}</option>
            ))}
          </select>
          <button className="btn" onClick={importXlsx} disabled={!importPath || importBusy}>
            {importBusy ? 'Parsing…' : 'Parse & Edit'}
          </button>
        </div>
        {refFiles.length === 0 && !busy && (
          <div className="muted" style={{ marginTop: 8 }}>No XLSX files found in PDI_Reference folder.</div>
        )}
      </div>

      {/* Template list */}
      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 12 }}>All Templates</div>
        {busy && <div className="muted">Loading…</div>}
        {templates.length === 0 && !busy && <div className="muted">No templates yet.</div>}

        {templates.map(t => (
          <div key={t.template} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 700 }}>
                {t.title}
                <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 6px', borderRadius: 4, background: t.source === 'file' ? '#f0fdf4' : '#eff6ff', color: t.source === 'file' ? '#166534' : '#1d4ed8', fontWeight: 600 }}>
                  {t.source === 'file' ? 'built-in' : 'custom'}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12 }}>
                Key: <code>{t.template}</code>
                {t.vendor && ` · ${t.vendor}`}
                {t.version && ` · v${t.version}`}
              </div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn-outline" onClick={() => api.get(`/pdi/templates/${t.template}`).then(r => setPreview(r.data)).catch(e => setMsg(e?.response?.data?.error || e.message))}>Preview</button>
              {t.source === 'db'
                ? <>
                    <button className="btn-outline" onClick={() => openEdit(t)}>Edit</button>
                    <button className="btn-outline" style={{ color: '#dc2626', borderColor: '#dc2626' }} onClick={() => deleteTemplate(t.template)}>Delete</button>
                  </>
                : <button className="btn-outline" onClick={() => {
                    // load file template then open editor as import copy
                    api.get(`/pdi/templates/${t.template}`).then(({ data }) => {
                      setEditTitle(data.title + ' (copy)');
                      setEditVendor(data.vendor || '');
                      setEditVersion(data.version || '');
                      setEditKey(data.template + '_custom');
                      setEditSections(data.sections || []);
                      setEditing('import');
                    });
                  }}>Copy &amp; Edit</button>
              }
            </div>
          </div>
        ))}
      </div>

      {/* Preview modal */}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18 }}>{preview.title}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  Key: <code>{preview.template}</code>
                  {preview.vendor && ` · ${preview.vendor}`}
                  {preview.version && ` · v${preview.version}`}
                  {' · '}{(preview.sections || []).reduce((n, s) => n + s.steps.length, 0)} steps across {(preview.sections || []).length} sections
                </div>
              </div>
              <button onClick={() => setPreview(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}>Close</button>
            </div>

            {(preview.sections || []).map((sec, si) => (
              <div key={si} style={{ marginBottom: 16 }}>
                <div style={{ background: '#1e293b', color: '#fff', padding: '6px 12px', borderRadius: '6px 6px 0 0', fontWeight: 700, fontSize: 13 }}>
                  {sec.title} <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.7 }}>({sec.steps.length} steps)</span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {sec.steps.map((step, stI) => (
                      <tr key={stI} style={{ background: stI % 2 === 0 ? '#f8fafc' : '#fff' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 700, width: 36, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{step.id}</td>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ fontWeight: 600 }}>{step.item}</div>
                          {step.instruction && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{step.instruction}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
